import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Spinner } from '@klaro7/ui';

import { useCallDetail, useDeleteTestCallRecording, useRetranscribeCall } from '@/features/revenue/api';
import { CallRecordingPlayer } from '@/features/revenue/CallRecordingPlayer';
import { CallNetDiagnosticsCard } from '@/features/revenue/CallNetDiagnosticsCard';
import { callStatusInfo } from '@/features/calls/lib/callStatusInfo';
import { DeviceIcon } from '@/features/calls/lib/deviceInfo';
import { TariffProvenanceDetail } from '@/features/calls/lib/tariffProvenance';

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
}

function formatEuro(n: number | null): string {
  return n == null ? '—' : `€ ${n.toFixed(2)}`;
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

const callTypeLabels: Record<string, string> = { audio: 'Audio', video: 'Video', chat: 'Chat' };
const endReasonLabels: Record<string, string> = {
  caller_hangup: 'Chiusa dal chiamante',
  callee_hangup: 'Chiusa dal consulente',
  caller_disconnect: 'Disconnessione chiamante',
  callee_disconnect: 'Disconnessione consulente',
  insufficient_credits: 'Credito esaurito',
  timeout: 'Timeout',
  max_duration: 'Durata massima raggiunta',
  manual_pause_timeout: 'Terminata dopo pausa non ripresa',
  error: 'Errore',
  admin_suspended: 'Sospesa da admin',
};

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-2">
      <dt className="text-sm text-neutral-500">{label}</dt>
      <dd className="text-right text-sm font-medium text-neutral-900">{value}</dd>
    </div>
  );
}

export function AdminCallDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const fromDate = searchParams.get('date');
  const backTo = fromDate ? `/dashboard/admin/revenue?date=${fromDate}` : '/dashboard/admin/revenue';
  const { data, isLoading, isError } = useCallDetail(id);
  const retranscribe = useRetranscribeCall(id);
  const deleteRecording = useDeleteTestCallRecording(id);
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (isLoading) {
    return (
      <div className="flex justify-center py-24">
        <Spinner size="lg" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="space-y-4 py-12 text-center">
        <p className="text-neutral-500">Chiamata non trovata.</p>
        <Link to={backTo} className="text-sm font-medium text-brand-teal hover:underline">
          ← Torna a Revenue
        </Link>
      </div>
    );
  }

  const isChat = data.callType === 'chat';
  const status = callStatusInfo({ status: data.status, connectedAt: data.connectedAt });

  return (
    <div className="space-y-6">
      <div>
        <Link to={backTo} className="text-sm font-medium text-brand-teal hover:underline">
          ← Torna a Revenue
        </Link>
      </div>

      {/* Header banner */}
      <div className="rounded-2xl bg-gradient-to-r from-brand-navy via-[#1c1a4d] to-brand-teal-600 p-6">
        <h1 className="text-2xl font-bold text-white">
          {callTypeLabels[data.callType] ?? data.callType} ·{' '}
          <Link to={`/dashboard/admin/users/${data.caller.id}`} className="hover:underline">
            {data.caller.displayName}
          </Link>{' '}
          →{' '}
          <Link to={`/dashboard/admin/professionals/${data.professional.id}`} className="hover:underline">
            {data.professional.displayName}
          </Link>
        </h1>
        <p className="mt-1 text-sm text-white/70">
          {formatDateTime(data.initiatedAt)} · {status.label}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Session info */}
        <div className="rounded-xl border border-neutral-200 bg-white p-5">
          <h2 className="text-base font-semibold text-neutral-900">Dettagli sessione</h2>
          <dl className="mt-3 divide-y divide-neutral-100">
            <div className="flex items-baseline justify-between gap-4 py-2">
              <dt className="text-sm text-neutral-500">Chiamante</dt>
              <dd className="text-right text-sm font-medium">
                <Link
                  to={`/dashboard/admin/users/${data.caller.id}`}
                  className="text-brand-teal hover:underline"
                >
                  {data.caller.displayName}
                </Link>
              </dd>
            </div>
            <div className="flex items-baseline justify-between gap-4 py-2">
              <dt className="text-sm text-neutral-500">Dispositivo chiamante</dt>
              <dd className="text-right text-sm font-medium text-neutral-900">
                <DeviceIcon userAgent={data.callerUserAgent} deviceModel={data.callerDeviceModel} />
              </dd>
            </div>
            <div className="flex items-baseline justify-between gap-4 py-2">
              <dt className="text-sm text-neutral-500">Consulente</dt>
              <dd className="text-right text-sm font-medium">
                <Link
                  to={`/dashboard/admin/professionals/${data.professional.id}`}
                  className="text-brand-teal hover:underline"
                >
                  {data.professional.displayName}
                </Link>
              </dd>
            </div>
            <div className="flex items-baseline justify-between gap-4 py-2">
              <dt className="text-sm text-neutral-500">Dispositivo consulente</dt>
              <dd className="text-right text-sm font-medium text-neutral-900">
                <DeviceIcon
                  userAgent={data.professionalUserAgent}
                  deviceModel={data.professionalDeviceModel}
                />
              </dd>
            </div>
            <InfoRow label="Tipo" value={callTypeLabels[data.callType] ?? data.callType} />
            <InfoRow label="Stato" value={status.label} />
            <InfoRow label="Risposta" value={data.answered ? 'Sì' : 'No'} />
            <InfoRow label="Inizio" value={formatDateTime(data.initiatedAt)} />
            <InfoRow label="Connessa" value={formatDateTime(data.connectedAt)} />
            <InfoRow label="Fine" value={formatDateTime(data.endedAt)} />
            <InfoRow label="Durata" value={formatDuration(data.durationSeconds)} />
            <InfoRow
              label="Motivo fine"
              value={data.endReason ? (endReasonLabels[data.endReason] ?? data.endReason) : '—'}
            />
          </dl>
        </div>

        {/* Amounts */}
        <div className="rounded-xl border border-neutral-200 bg-white p-5">
          <h2 className="text-base font-semibold text-neutral-900">Importi</h2>
          <dl className="mt-3 divide-y divide-neutral-100">
            <div className="flex items-start justify-between gap-4 py-2">
              <dt className="text-sm text-neutral-500">Provenienza tariffa</dt>
              <dd className="text-right">
                <TariffProvenanceDetail segments={data.tariff.segments} />
              </dd>
            </div>
            <InfoRow label="Pagato dal cliente" value={formatEuro(data.customerPaid)} />
            <InfoRow label="Quota consulente" value={formatEuro(data.consultantEarning)} />
            <InfoRow label="Quota piattaforma" value={formatEuro(data.platformEarning)} />
            {data.conversionEarning != null && (
              <InfoRow
                label="Bonus conversione (last-touch)"
                value={`+${formatEuro(data.conversionEarning)}`}
              />
            )}
          </dl>
        </div>
      </div>

      {/* Diagnostica connessione (solo audio/video: la chat non ha media WebRTC) */}
      {!isChat && (
        <CallNetDiagnosticsCard caller={data.callerNetDiag} professional={data.professionalNetDiag} />
      )}

      {/* Transcription / chat messages */}
      <div className="rounded-xl border border-neutral-200 bg-white">
        <div className="flex items-center justify-between gap-4 border-b border-neutral-100 px-5 py-4">
          <h2 className="text-base font-semibold text-neutral-900">
            {isChat ? 'Messaggi della sessione' : 'Trascrizione'}
          </h2>
          {!isChat && data.hasRecording && (
            <button
              type="button"
              onClick={() => retranscribe.mutate()}
              disabled={retranscribe.isPending || data.transcriptionStatus === 'processing'}
              className="rounded-lg bg-brand-teal px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-brand-teal/90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {retranscribe.isPending || data.transcriptionStatus === 'processing'
                ? 'In elaborazione…'
                : 'Rigenera trascrizione'}
            </button>
          )}
        </div>
        <div className="p-5">
          {retranscribe.isError && (
            <p className="mb-3 text-sm text-red-600">Impossibile avviare la trascrizione. Riprova.</p>
          )}
          {isChat ? (
            !data.messages || data.messages.length === 0 ? (
              <p className="py-6 text-center text-sm text-neutral-400">Nessun messaggio in questa sessione.</p>
            ) : (
              <div className="space-y-3">
                {data.messages.map((m, i) => (
                  <div key={i} className={`flex ${m.sender === 'professional' ? 'justify-end' : 'justify-start'}`}>
                    <div
                      className={`max-w-[75%] rounded-2xl px-4 py-2 text-sm ${
                        m.sender === 'professional'
                          ? 'bg-brand-teal/10 text-neutral-900'
                          : 'bg-neutral-100 text-neutral-900'
                      }`}
                    >
                      <p className="whitespace-pre-wrap">{m.content}</p>
                      <p className="mt-1 text-right text-[11px] text-neutral-400">
                        {m.sender === 'professional' ? data.professional.displayName : data.caller.displayName} ·{' '}
                        {formatTime(m.at)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : data.transcriptionStatus === 'completed' &&
            data.transcriptionSegments &&
            data.transcriptionSegments.length > 0 ? (
            <div className="space-y-3">
              {data.transcriptionSegments.map((s, i) => (
                <div
                  key={i}
                  className={`flex ${
                    s.speaker === 'professional'
                      ? 'justify-end'
                      : s.speaker === 'caller'
                        ? 'justify-start'
                        : 'justify-center'
                  }`}
                >
                  <div
                    className={`max-w-[75%] rounded-2xl px-4 py-2 text-sm ${
                      s.speaker === 'professional'
                        ? 'bg-brand-teal/10 text-neutral-900'
                        : s.speaker === 'caller'
                          ? 'bg-neutral-100 text-neutral-900'
                          : 'bg-neutral-50 italic text-neutral-500'
                    }`}
                  >
                    <p className="whitespace-pre-wrap">{s.text}</p>
                    <p className="mt-1 text-right text-[11px] text-neutral-400">
                      {s.speaker === 'professional'
                        ? data.professional.displayName
                        : s.speaker === 'caller'
                          ? data.caller.displayName
                          : 'Non identificato'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : data.transcriptionStatus === 'completed' && data.transcription ? (
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-neutral-800">{data.transcription}</p>
          ) : data.transcriptionStatus === 'processing' ? (
            <p className="py-6 text-center text-sm text-amber-600">Trascrizione in elaborazione…</p>
          ) : (
            <p className="py-6 text-center text-sm text-neutral-400">Trascrizione non disponibile.</p>
          )}
          {!isChat && data.hasRecording && (
            <div className="mt-5 border-t border-neutral-100 pt-4">
              <h3 className="mb-2 text-sm font-semibold text-neutral-900">Registrazione</h3>
              <CallRecordingPlayer callId={data.id} />
            </div>
          )}
          {/* Solo chiamate di prova (consulente di test): il server rifiuta qualunque altra. */}
          {!isChat && data.isTest && (data.hasRecording || data.transcription) && (
            <div className="mt-5 border-t border-neutral-100 pt-4">
              <h3 className="mb-1 text-sm font-semibold text-neutral-900">Chiamata di prova</h3>
              <p className="mb-3 text-sm text-neutral-600">
                Puoi cancellare audio e trascrizione. La chiamata resta nello storico.
              </p>
              {!confirmDelete ? (
                <button
                  type="button"
                  onClick={() => setConfirmDelete(true)}
                  className="rounded-lg border border-red-300 px-3 py-1.5 text-sm font-medium text-red-700 transition-colors hover:bg-red-50"
                >
                  Cancella audio e trascrizione
                </button>
              ) : (
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-sm text-neutral-700">Sei sicuro? Non si può annullare.</span>
                  <button
                    type="button"
                    onClick={() => deleteRecording.mutate(undefined, { onSuccess: () => setConfirmDelete(false) })}
                    disabled={deleteRecording.isPending}
                    className="rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {deleteRecording.isPending ? 'Cancello…' : 'Sì, cancella'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmDelete(false)}
                    disabled={deleteRecording.isPending}
                    className="rounded-lg border border-neutral-300 px-3 py-1.5 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
                  >
                    Annulla
                  </button>
                </div>
              )}
              {deleteRecording.isError && (
                <p className="mt-2 text-sm text-red-600">Impossibile cancellare. Riprova tra poco.</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
